import type { Sample } from "./math";

function param(n: number, fn: (t: number) => Sample): Sample[] {
  const pts: Sample[] = [];
  for (let i = 0; i < n; i++) pts.push(fn((i / n) * Math.PI * 2));
  return pts;
}

export function circle(): Sample[] {
  return param(128, (t) => ({ x: Math.cos(t), y: Math.sin(t) }));
}

export function square(): Sample[] {
  const sides = [
    [0, 0, 1, 0],
    [1, 0, 1, 1],
    [1, 1, 0, 1],
    [0, 1, 0, 0],
  ];
  const pts: Sample[] = [];
  for (const [x0, y0, x1, y1] of sides) {
    for (let i = 0; i < 32; i++) {
      const u = i / 32;
      pts.push({ x: x0 + (x1 - x0) * u, y: y0 + (y1 - y0) * u });
    }
  }
  return pts;
}

export function heart(): Sample[] {
  return param(180, (t) => {
    const x = 16 * Math.sin(t) ** 3;
    const y = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
    return { x, y };
  });
}

export function star(): Sample[] {
  const pts: Sample[] = [];
  const spikes = 5;
  for (let i = 0; i < spikes * 2; i++) {
    const r = i % 2 === 0 ? 1 : 0.42;
    const a = -Math.PI / 2 + (i * Math.PI) / spikes;
    pts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
  }
  return pts;
}

/** Trefoil knot projection — a closed 2D curve. */
export function trefoil(): Sample[] {
  return param(220, (t) => ({
    x: Math.sin(t) + 2 * Math.sin(2 * t),
    y: Math.cos(t) - 2 * Math.cos(2 * t),
  }));
}

export function infinity(): Sample[] {
  return param(180, (t) => {
    const s = 1 + Math.sin(t) ** 2;
    return { x: Math.cos(t) / s, y: (Math.sin(t) * Math.cos(t)) / s };
  });
}

/** Closed outline of a bold π glyph. */
export function pi(): Sample[] {
  return [
    { x: 8, y: 88 },
    { x: 8, y: 22 },
    { x: 4, y: 18 },
    { x: 4, y: 8 },
    { x: 96, y: 8 },
    { x: 96, y: 18 },
    { x: 78, y: 18 },
    { x: 78, y: 88 },
    { x: 66, y: 88 },
    { x: 66, y: 18 },
    { x: 38, y: 18 },
    { x: 38, y: 88 },
    { x: 26, y: 88 },
    { x: 26, y: 18 },
    { x: 20, y: 18 },
    { x: 20, y: 88 },
  ];
}

function arc(cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, n: number): Sample[] {
  const pts: Sample[] = [];
  for (let i = 0; i <= n; i++) {
    const t = a0 + ((a1 - a0) * i) / n;
    pts.push({ x: cx + rx * Math.cos(t), y: cy + ry * Math.sin(t) });
  }
  return pts;
}

/** Sitting cat, side-on — the demo hook. */
export function cat(): Sample[] {
  const pts: Sample[] = [];
  pts.push({ x: 34, y: 22 }, { x: 24, y: 2 }, { x: 40, y: 18 });
  pts.push(...arc(48, 32, 18, 16, -2.5, -0.15, 14));
  pts.push({ x: 62, y: 18 }, { x: 76, y: 0 }, { x: 74, y: 22 });
  pts.push(...arc(88, 54, 32, 26, -1.35, 1.15, 22));
  pts.push(
    { x: 118, y: 58 },
    { x: 138, y: 42 },
    { x: 146, y: 22 },
    { x: 138, y: 8 },
    { x: 126, y: 16 },
    { x: 132, y: 30 },
    { x: 122, y: 46 },
    { x: 108, y: 58 },
  );
  pts.push(...arc(88, 54, 32, 26, 1.15, Math.PI + 0.55, 20));
  pts.push({ x: 52, y: 68 }, { x: 44, y: 52 });
  pts.push(...arc(48, 32, 18, 16, 1.4, Math.PI + 0.55, 10));
  return pts;
}

export type PresetId = "cat" | "heart" | "pi" | "star" | "square" | "trefoil" | "infinity" | "circle";

export const PRESETS: { id: PresetId; label: string; path: () => Sample[] }[] = [
  { id: "cat", label: "Cat", path: cat },
  { id: "heart", label: "Heart", path: heart },
  { id: "pi", label: "π", path: pi },
  { id: "star", label: "Star", path: star },
  { id: "square", label: "Square", path: square },
  { id: "trefoil", label: "Trefoil", path: trefoil },
  { id: "infinity", label: "Infinity", path: infinity },
  { id: "circle", label: "Circle", path: circle },
];

export function presetPath(id: PresetId): Sample[] {
  const p = PRESETS.find((x) => x.id === id);
  return p ? p.path() : cat();
}
